import React, { useState, useCallback, useRef, useEffect } from 'react';
import VoiceOrb from './components/VoiceOrb';
import Transcript from './components/Transcript';
import Settings from './components/Settings';
import AudiobookPanel from './components/AudiobookPanel';
import useConversation from './hooks/useConversation';

const JARVIS_PROMPT = 'You are Jarvis, Dave\'s voice assistant. ABSOLUTE RULE: Maximum ONE sentence per spoken response. If you can say it in 5 words, use 5 words. Never apologize. Never ask follow-up questions unless essential. Never explain what you cannot do — just answer what you can. Style: calm British cadence, address Dave as "sir", subtly witty. Team: Bishop builds code on M2, Friday handles Telegram. Agency: Build A Better Website LLC, 111 sites. You can send emails, save email drafts to Dave\'s Gmail, read project memory files, and see images Dave shares. When Dave asks you to draft, compose, or write up an email, do NOT call a tool — instead write the email inside a draft block in EXACTLY this format, each marker on its own line:\n<<DRAFT>>\nTo: recipient@example.com\nSubject: The subject line\nBody:\nThe full email body, which may be as long as needed and span multiple lines.\n<<END_DRAFT>>\nLeave the To: value blank if Dave gave no recipient. The draft block is saved to Dave\'s Gmail Drafts automatically and is stripped before text-to-speech, so it is never read aloud — outside the block, say only one short sentence such as "Draft saved to your Gmail, sir."';

const DEFAULT_SETTINGS = {
  workerUrl: import.meta.env.VITE_WORKER_URL || 'https://hal-voice.dave-d42.workers.dev',
  ttsUrl: 'https://tts.betterwebsite.dev',
  voiceId: 'JBFqnCBsd6RMkjVDRZzb', // ElevenLabs George voice
  systemPrompt: JARVIS_PROMPT,
  pushToTalk: false,
};

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

export default function App() {
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(() => {
    localStorage.removeItem('hal-voice-settings');
    return DEFAULT_SETTINGS;
  });
  const [pendingImage, setPendingImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const clearImage = useCallback(() => setPendingImage(null), []);

  const [textInput, setTextInput] = useState('');
  const textInputRef = useRef(null);

  const {
    state, mode, userMuted, wordPulse, transcript, streamedResponse,
    history, micLevel, noiseGateOpen,
    start, stop, startListening, stopListening, sendText, clearHistory,
    toggleUserMute, switchMode, setNoiseGateThreshold,
  } = useConversation(settings, { pendingImage, clearImage });

  const processFile = useCallback((file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert('Unsupported file type. Please use PNG, JPEG, GIF, or WebP.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      alert('Image too large. Maximum size is 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setPendingImage({ base64, mediaType: file.type, fileName: file.name });
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  }, [processFile]);

  // Auto-disconnect when app goes to background (phone locked, app switched)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && state !== 'idle') {
        stop();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [state, stop]);

  // Single orb tap handles everything (voice mode only)
  const handleOrbTap = useCallback(() => {
    if (mode === 'text') return; // Orb is decorative in text mode
    if (state === 'idle') {
      start();
    } else if (state === 'ready') {
      startListening();
    } else if (state === 'listening') {
      stopListening();
    }
  }, [state, mode, start, startListening, stopListening]);

  const handleSendText = useCallback(() => {
    if (!textInput.trim()) return;
    sendText(textInput.trim());
    setTextInput('');
  }, [textInput, sendText]);

  // Focus text input when switching to text mode
  useEffect(() => {
    if (mode === 'text' && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [mode]);

  const handleSaveSettings = useCallback((newSettings) => {
    setSettings(newSettings);
    setShowSettings(false);
  }, []);

  const handleClearHistory = useCallback(() => {
    if (state !== 'idle') stop();
    clearHistory();
  }, [state, stop, clearHistory]);

  const stateLabel = {
    idle: 'Standby',
    ready: 'Ready',
    listening: 'Listening',
    processing: 'Processing',
    speaking: 'Speaking',
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Jarvis</h1>
        {/* Voice / Text mode toggle */}
        <div className="mode-toggle">
          <button
            className={`mode-btn ${mode === 'voice' ? 'active' : ''}`}
            onClick={() => switchMode('voice')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
            <span>Voice</span>
          </button>
          <button
            className={`mode-btn ${mode === 'text' ? 'active' : ''}`}
            onClick={() => switchMode('text')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>Text</span>
          </button>
        </div>
      </header>

      {/* Voice mode UI */}
      {mode === 'voice' && (
        <>
          <div className="orb-section">
            <div
              className={`orb-container ${isDragging ? 'dragging' : ''}`}
              onClick={handleOrbTap}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <VoiceOrb state={state} wordPulse={wordPulse} isDragging={isDragging} />
              <span className={`status-label ${state}`}>{stateLabel[state]}</span>
              {state === 'idle' && <span className="tap-prompt">TAP TO ACTIVATE</span>}
              {state === 'ready' && <span className="tap-prompt">TAP TO SPEAK</span>}
              {state === 'listening' && <span className="tap-prompt">TAP TO SEND</span>}
            </div>

            {/* Action buttons row */}
            <div className="action-buttons">
              {/* Mute toggle button */}
              <button
                className={`action-btn mute-btn ${userMuted ? 'muted' : ''}`}
                onClick={(e) => { e.stopPropagation(); toggleUserMute(); }}
                title={userMuted ? 'Unmute microphone' : 'Mute microphone'}
              >
                {userMuted ? (
                  /* Mic off icon */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="1" y1="1" x2="23" y2="23" />
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.5-.35 2.18" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                ) : (
                  /* Mic on icon */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                )}
              </button>
              {/* Image upload button */}
              <button
                className="action-btn"
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                title="Attach image"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </button>
            </div>

            {/* Noise gate level indicator */}
            {state === 'listening' && (
              <div className="noise-gate-indicator">
                <div className="level-bar">
                  <div
                    className={`level-fill ${noiseGateOpen ? 'gate-open' : 'gate-closed'}`}
                    style={{ width: `${Math.min(100, micLevel * 100)}%` }}
                  />
                </div>
                <span className="gate-label">{noiseGateOpen ? 'VOICE' : 'GATE'}</span>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Pending image thumbnail */}
            {pendingImage && (
              <div className="image-preview">
                <img
                  src={`data:${pendingImage.mediaType};base64,${pendingImage.base64}`}
                  alt={pendingImage.fileName}
                />
                <button className="image-preview-remove" onClick={(e) => { e.stopPropagation(); clearImage(); }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.5">
                    <line x1="2" y1="2" x2="8" y2="8" />
                    <line x1="8" y1="2" x2="2" y2="8" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <Transcript history={history} partial={transcript} streamedResponse={streamedResponse} />
        </>
      )}

      {/* Text mode UI */}
      {mode === 'text' && (
        <div className="text-mode">
          <div className="text-mode-orb">
            <VoiceOrb state={state} wordPulse={false} isDragging={false} />
          </div>
          <div className="text-mode-transcript" ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
            <Transcript history={history} partial="" streamedResponse={streamedResponse} />
          </div>

          {/* Pending image thumbnail in text mode */}
          {pendingImage && (
            <div className="text-mode-image-preview">
              <img
                src={`data:${pendingImage.mediaType};base64,${pendingImage.base64}`}
                alt={pendingImage.fileName}
              />
              <button className="image-preview-remove" onClick={clearImage}>
                <svg width="10" height="10" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.5">
                  <line x1="2" y1="2" x2="8" y2="8" />
                  <line x1="8" y1="2" x2="2" y2="8" />
                </svg>
              </button>
            </div>
          )}

          <div className="text-mode-input-bar">
            <button
              className="action-btn"
              onClick={() => fileInputRef.current?.click()}
              title="Attach image"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </button>
            <input
              ref={textInputRef}
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendText(); }}
              placeholder="Message Jarvis..."
              className="text-input"
              disabled={state === 'processing'}
            />
            <button
              className="text-send-btn"
              onClick={handleSendText}
              disabled={!textInput.trim() || state === 'processing'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}

      {/* PDF audiobook — hand Bishop a PDF and it reads it aloud */}
      <AudiobookPanel />

      <footer className="app-footer">Jarvis</footer>

      {showSettings && (
        <Settings
          settings={settings}
          onSave={handleSaveSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}
